import { CaretDownIcon } from '@phosphor-icons/react';
import type { ReactNode, SelectHTMLAttributes } from 'react';

import { Field } from '../Field/Field';
import styles from './Select.module.css';

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> {
  label: string;
  /** Метка видна не всегда: в панели инструментов она дублирует контекст. */
  hideLabel?: boolean | undefined;
  hint?: string | undefined;
  error?: string | undefined;
  children: ReactNode;
  className?: string | undefined;
}

/**
 * Выпадающий список на нативном `<select>` (DESIGN_SYSTEM §5.7).
 *
 * Нативный элемент, а не своя реализация: на мобильных браузер показывает
 * системный пикер, клавиатура и скринридеры работают без дополнительного кода.
 * Метка всегда есть в разметке — плейсхолдер вместо метки не используется.
 */
export function Select({
  label,
  hideLabel,
  hint,
  error,
  children,
  className,
  id,
  ...rest
}: SelectProps) {
  return (
    <Field
      label={label}
      hideLabel={hideLabel}
      hint={hint}
      error={error}
      id={id}
      className={className}
    >
      {({ fieldId, describedBy, invalid }) => (
        <div className={styles.control}>
          <select
            id={fieldId}
            className={[styles.select, invalid && styles.selectError].filter(Boolean).join(' ')}
            aria-invalid={invalid ? true : undefined}
            aria-describedby={describedBy}
            {...rest}
          >
            {children}
          </select>
          <CaretDownIcon size={16} aria-hidden="true" className={styles.caret} />
        </div>
      )}
    </Field>
  );
}
